<template>
    <div>
        <general-order-details :return-url="returnUrl"
                               :create-order-url="createOrderUrl"
                               :edit-order-url-template="editOrderUrlTemplate"
                               :api-base-url="apiBaseUrl"
                               :file-download-url="fileDownloadUrl"
                               :current-user-account-id="currentUserAccountId"
                               :order="order"
                               :selectable-clients="selectableClients"
                               :initial-client-manages-assigned-contracts="initialClientManagesAssignedContracts"
                               :initial-client-branches="initialClientBranches"
                               :initial-ordering-client-contact="initialOrderingClientContact"
                               :initial-billing-contact="initialBillingContact"
                               :selectable-service-types="currentSelectableServiceTypes"
                               service-types-null-select-placeholder="Select a court/tribunal/other first"
                               :selectable-service-delays="currentSelectableServiceDelays"
                               :selectable-non-production-charge-types="currentSelectableNonProductionChargeTypes"
                               :possible-non-production-dates="null"
                               :non-production-charges="nonProductionCharges"
                               :attachments="attachments"
                               :all-attachment-classifications="allAttachmentClassifications"
                               :all-transcript-delivery-options="allTranscriptDeliveryOptions"
                               :default-delivery-contacts="currentDefaultDeliveryContacts"
                               :initial-delivery-contacts="initialDeliveryContacts"
                               :initial-delivery-cc-contacts="initialDeliveryCcContacts"
                               :initial-delivery-bcc-contacts="initialDeliveryBccContacts"
                               :delivery-contact-ids="deliveryContactIds"
                               :delivery-contact-cc-ids="deliveryContactCcIds"
                               :delivery-contact-bcc-ids="deliveryContactBccIds"
                               :emails-link="emailsLink"
                               :all-audio-sub-status-options="allAudioSubStatusOptions"
                               :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                               :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                               :all-deposit-sub-status-options="allDepositSubStatusOptions"
                               :all-vetting-sub-status-options="allVettingSubStatusOptions"
                               :can-edit-order="canEditOrder"
                               :can-view-attachments="canViewAttachments"
                               :can-create-comment="canCreateComment"
                               :can-create-billing-information-comment="canCreateBillingInformationComment"
                               :can-edit-and-retract-all-comments="canEditAndRetractAllComments"
                               :can-view-sub-status-details="canViewSubStatusDetails"
                               :can-modify-audio-sub-status="canModifyAudioSubStatus"
                               :can-modify-log-notes-sub-status="canModifyLogNotesSubStatus"
                               :can-modify-release-approval-sub-status="canModifyReleaseApprovalSubStatus"
                               :can-modify-deposit-sub-status="canModifyDepositSubStatus"
                               :can-modify-vetting-sub-status="canModifyVettingSubStatus"
                               :hide-distribution-list="true"
                               :hide-default-distribution-list="true"
                               :readonly="readonly"
                               :validation-observer="validationObserver"
                               :save="saveOrder"
                               v-on:ordering-client-change="orderingClientChanged">
            <b-form-row>
                <validation-provider rules="required" name="Court/Tribunal/Other" v-slot="vp" slim>
                    <b-form-group label="Court/Tribunal/Other"
                                  label-for="courtRegistryId"
                                  class="col-md-4">
                        <select2 name="courtRegistryId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || isUuidNullOrEmpty(orderingClientId)"
                                 :options="initialCourtRegistries"
                                 enable-ajax
                                 :placeholder="isUuidNullOrEmpty(orderingClientId) ? 'Select a client first' : 'Select a court/tribunal/other'"
                                 key-property="courtRegistryId"
                                 text-property="name"
                                 v-model="courtRegistryId"
                                 v-on:search="findCourtRegistries"
                                 v-on:select="courtRegistrySelected"
                                 :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(orderingClientId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group class="col-md-4 align-self-end">
                    <b-form-checkbox name="isExempt"
                                     v-model="isExempt"
                                     class="mb-2"
                                     switch
                                     :disabled="readonly || !canEditOrder">
                        Fee Exempt
                    </b-form-checkbox>
                </b-form-group>
            </b-form-row>
            <b-form-row>
                <validation-provider rules="required" name="Jurisdiction" v-slot="vp" slim>
                    <b-form-group label="Jurisdiction"
                                  label-for="courtJurisdictionId"
                                  class="col-md-3">
                        <select2 name="courtJurisdictionId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || isUuidNullOrEmpty(courtRegistryId)"
                                 :options="initialCourtJurisdictions"
                                 enable-ajax
                                 :placeholder="isUuidNullOrEmpty(courtRegistryId) ? 'Select a court/tribunal/other first' : 'Select a jurisdiction'"
                                 key-property="courtJurisdictionId"
                                 text-property="name"
                                 v-model="courtJurisdictionId"
                                 v-on:search="findJurisdictions"
                                 v-on:select="courtJurisdictionSelected"
                                 :state="isUuidNullOrEmpty(courtRegistryId) ? null : vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(courtRegistryId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider rules="required" name="Court Room" v-slot="vp" slim>
                    <b-form-group label="Court Room"
                                  label-for="courtRoomId"
                                  class="col-md-3">
                        <select2 name="courtRoomId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || isUuidNullOrEmpty(courtRegistryId)"
                                 :options="initialCourtRooms"
                                 enable-ajax
                                 :placeholder="isUuidNullOrEmpty(courtRegistryId) ? 'Select a court/tribunal/other first' : 'Select a room'"
                                 key-property="courtRoomId"
                                 text-property="courtLocationRoomName"
                                 v-model="courtRoomId"
                                 v-on:search="findCourtRooms"
                                 :state="isUuidNullOrEmpty(courtRegistryId) ? null : vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="isUuidNullOrEmpty(courtRegistryId) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider ref="sittingDateValidator" rules="required" name="Sitting Date" v-slot="vp" slim>
                    <b-form-group label="Sitting Date"
                                  label-for="sittingDate"
                                  class="col-md-2">
                        <ejs-datepicker name="sittingDate"
                                        :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                                        v-model="sittingDate"
                                        format="d/M/yyyy"></ejs-datepicker>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group label="Start Time"
                              label-for="startTime"
                              class="col-md-2">
                    <b-form-input name="startTime"
                                  :disabled="readonly || !canEditOrder"
                                  type="time"
                                  step="1"
                                  v-model="startTime">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="End Time"
                              label-for="endTime"
                              class="col-md-2">
                    <b-form-input name="endTime"
                                  :disabled="readonly || !canEditOrder"
                                  type="time"
                                  step="1"
                                  v-model="endTime">
                    </b-form-input>
                </b-form-group>
            </b-form-row>
   
            <b-form-row>
                <validation-provider rules="required" name="Presiding Officers" v-slot="{ valid, errors }" slim>
                    <b-form-group label="Presiding Officers"
                                  label-for="presidingOfficers"
                                  class="col-md-12">
                        <select2 name="presidingOfficers"
                                 :disabled="readonly || !canEditOrder || isUuidNullOrEmpty(courtRegistryId)"
                                 placeholder="Select one or more presiding officer/s"
                                 v-model="assignedCourtJudgeIds"
                                 :options="initialCourtJudges"
                                 enable-ajax
                                 multiple
                                 key-property="courtJudgeId"
                                 text-property="displayName"
                                 v-on:search="findJudges"
                                 :state="errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-form-row>

            <b-row>
                <b-form-group label="Description"
                              label-for="description"
                              class="col-md-12">
                    <b-form-textarea name="description"
                                     placeholder="Enter a description"
                                     :disabled="readonly || !canEditOrder"
                                     v-model="description"></b-form-textarea>
                </b-form-group>
            </b-row>

        </general-order-details>
    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import { DatePickerPlugin, DateTimePickerPlugin } from '@syncfusion/ej2-vue-calendars';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);

    import Select2 from '../Select2';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import UuidMixins from '../../mixins/UuidMixins';

    import GeneralOrderDetails from './GeneralOrderDetails.vue'
    
    Vue.use(BootstrapVue);
    Vue.use(DatePickerPlugin);
    Vue.use(DateTimePickerPlugin);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('general-order-details', GeneralOrderDetails);

    export default {
        name: 'ListCourtOrderDetails',
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        props: {
            returnUrl: {
                type: String,
                required: true
            },
            createOrderUrl: {
                type: String,
                required: true
            },
            editOrderUrlTemplate: {
                type: String,
                required: true
            },
            apiBaseUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            order: {
                type: Object,
                required: true
            },
            selectableClients: {
                type: Array,
                default: null
            },
            initialClientManagesAssignedContracts: {
                type: Boolean,
                default: false
            },
            initialClientBranches: {
                type: Array,
                default: null
            },
            initialOrderingClientContact: {
                type: Object,
                default: null
            },
            initialBillingContact: {
                type: Object,
                default: null
            },
            initialCourtRegistry: {
                type: Object,
                default: null
            },
            initialCourtJurisdiction: {
                type: Object,
                default: null
            },
            initialCourtRoom: {
                type: Object,
                default: null
            },
            initialCourtJudges: {
                type: Array,
                default: null
            },
            initialServiceTypes: {
                type: Array,
                default: null
            },
            serviceTypesNullSelectPlaceholder: {
                type: String,
                default: null
            },
            initialServiceDelays: {
                type: Array,
                default: null
            },
            initialNonProductionChargeTypes: {
                type: Array,
                default: null
            },
            initialPossibleNonProductionDates: {
                type: Array,
                default: null
            },
            nonProductionCharges: {
                type: Array,
                default: null
            },
            defaultDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryCcContacts: {
                type: Array,
                default: null
            },
            initialDeliveryBccContacts: {
                type: Array,
                default: null
            },
            deliveryContactIds: {
                type: Array,
                default: null
            },
            deliveryContactCcIds: {
                type: Array,
                default: null
            },
            deliveryContactBccIds: {
                type: Array,
                default: null
            },
            emailsLink: {
                type: String,
                default: null
            },
            allAttachmentClassifications: {
                type: Array,
                default: null
            },
            allTranscriptDeliveryOptions: {
                type: Array,
                default: null
            },
            attachments: {
                type: Array,
                default: null
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            canEditOrder: {
                type: Boolean,
                requird: true
            },
            canViewAttachments: {
                type: Boolean,
                requird: true
            },
            canCreateComment: {
                type: Boolean,
                default: false
            },
            canCreateBillingInformationComment: {
                type: Boolean,
                default: false
            },
            canEditAndRetractAllComments: {
                type: Boolean,
                default: false
            },
            canViewSubStatusDetails: {
                type: Boolean,
                default: false
            },
            canModifyAudioSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyLogNotesSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyReleaseApprovalSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyDepositSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyVettingSubStatus: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: false 
            },
        },
        data: function () {
            return {
                orderingClientId: this.isUuidNullOrEmpty(this.order.orderingClientId) ? null : this.order.orderingClientId,
                initialCourtRegistries: (this.initialCourtRegistry == null) ? null : [this.initialCourtRegistry],
                courtRegistryId: this.isUuidNullOrEmpty(this.order.courtRegistryId) ? null : this.order.courtRegistryId,
                isExempt: this.order.isExempt,
                initialCourtJurisdictions: (this.initialCourtJurisdiction == null) ? null : [ this.initialCourtJurisdiction ],
                courtJurisdictionId: (this.order.listCourt != null) ? this.order.listCourt.courtJurisdictionId : null,
                initialCourtRooms: (this.initialCourtRoom == null) ? null : [ this.initialCourtRoom ],
                courtRoomId: (this.order.listCourt != null) ? this.order.listCourt.courtRoomId : null,
                sittingDate: (this.order.listCourt != null) ? this.parseDateTime(this.order.listCourt.sittingDate, null, false, false) : null,
                startTime: (this.order.listCourt != null) ? this.formatDateTime(this.order.listCourt.startTime, 'HH:mm:ss', true, true) : null,
                endTime: (this.order.listCourt != null) ? this.formatDateTime(this.order.listCourt.endTime, 'HH:mm:ss', true, true) : null,
                assignedCourtJudgeIds: (this.order.listCourt != null) ? this.order.listCourt.assignedJudgeIds : null,
                description: (this.order.listCourt != null) ? this.order.listCourt.description : null,
                currentSelectableServiceTypes: this.initialServiceTypes,
                currentSelectableServiceDelays: this.initialServiceDelays,
                currentSelectableNonProductionChargeTypes: this.initialNonProductionChargeTypes,
                currentDefaultDeliveryContacts: this.defaultDeliveryContacts
            };
        },
        computed: {
            validationObserver: function () {
                return this.$refs.validationObserver;
            }
        },
        methods: {
            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += apiRelativeUrl;
                return url;
            },
            orderingClientChanged: function (newOrderingClientId) {
                this.orderingClientId = newOrderingClientId;
            },
            findCourtRegistries: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.apiControllerUrl('courtregistry');
                url += '/byclient/' + this.orderingClientId + '/find';
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtRegistryId;
                                obj.text = obj.name;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            courtRegistrySelected: function () {
                this.courtJurisdictionId = null;
                this.courtRoomId = null;
                this.reloadServiceTypes();
                this.reloadServiceDelays();
                this.reloadNonProductionChargeTypes();
                this.reloadDefaultDeliveryContacts();

                Vue.nextTick().then(() => {
                    this.$refs.sittingDateValidator.reset();
                });
            },
            findJurisdictions: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.apiControllerUrl('courtjurisdiction');
                url += '/bycourtregistry/' + this.courtRegistryId + '/find';
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtJurisdictionId;
                                obj.text = obj.name;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            courtJurisdictionSelected: function () {
                this.reloadDefaultDeliveryContacts();
            },
            findCourtRooms: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.apiControllerUrl('courtroom');
                url += '/bycourtregistry/' + this.courtRegistryId + '/find';
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtRoomId;
                                obj.text = obj.courtLocationRoomName;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            findJudges: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.apiControllerUrl('courtjudge') + '/bycourtregistry/' + this.courtRegistryId + '/find';
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtJudgeId;
                                obj.text = obj.displayName;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            reloadServiceTypes: function () {
                if (this.courtRegistryId) {
                    var url = this.apiControllerUrl('servicetype');
                    url += '/bycourtregistry/' + this.courtRegistryId + '/forlistcourtorder';
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceTypes = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceTypes = null;
                }
            },
            reloadServiceDelays: function () {
                if (this.courtRegistryId) {
                    var url = this.apiControllerUrl('servicedelay');
                    url += '/bycourtregistry/' + this.courtRegistryId;
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceDelays = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceDelays = null;
                }

            },
            reloadNonProductionChargeTypes: function () {
                if (this.courtRegistryId) {
                    var url = this.apiControllerUrl('nonproductionchargetype');
                    url += '/bycourtregistry/' + this.courtRegistryId + '/rates/true';
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableNonProductionChargeTypes = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableNonProductionChargeTypes = null;
                }

            },
            reloadDefaultDeliveryContacts: function () {
                if (this.courtJurisdictionId) {
                    var url = this.apiControllerUrl('contact');
                    url += '/jurisdictiondelivery/bycourtjurisdiction/' + this.courtJurisdictionId;
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentDefaultDeliveryContacts = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentDefaultDeliveryContacts = null;
                }
            },
            //reload: function () {
            //    var assignmentsVisible = (this.canViewAllDetails || this.canManageAssignments) && (this.workItem != null);
            //    var commentsVisible = (this.workItem != null);
            //    if (this.relatedCourtMatterId) {
            //        if (this.$refs.courtMatterDetails) this.$refs.courtMatterDetails.reload();
            //        if (this.$refs.courtAppearances) this.$refs.courtAppearances.reload();
            //    }
            //    if (this.relatedCourtSittingId && this.$refs.courtSittingDetails) this.$refs.courtSittingDetails.reload();
            //    if (this.relatedInterviewId && this.$refs.interviewDetails) this.$refs.interviewDetails.reload();
            //    if (assignmentsVisible && this.$refs.staffWorkItemAssignments) this.$refs.staffWorkItemAssignments.reload();
            //    if (commentsVisible && this.$refs.comments) this.$refs.comments.reload();
            //    if (this.canViewAttachments && this.$refs.workItemAttachments) this.$refs.workItemAttachments.reload();
            //}
            saveOrder: function (generalData) {
                var data = {
                    ...generalData,
                    listCourt: {
                        courtJurisdictionId: this.courtJurisdictionId,
                        courtRoomId: this.courtRoomId,
                        sittingDate: this.sittingDate,
                        startTime: this.startTime,
                        endTime: this.endTime,
                        description: this.description
                    },
                    assignedCourtJudgeIds: this.assignedCourtJudgeIds
                };
                data.order.courtRegistryId = this.courtRegistryId;
                data.order.isExempt = this.isExempt;
                if (this.order.listCourt) data.listCourt.listCourtId = this.order.listCourt.listCourtId;
                if (data.listCourt.sittingDate != null) {
                    //the input from the UI control has a time of 00:00:00 but the local timezone, to get rid of the timezone we have to
                    //convert the date to a string first and then create our own date/time object from it
                    data.listCourt.sittingDate = this.parseDateTime(this.formatDateTime(data.listCourt.sittingDate, 'DD/MM/YYYY'), 'DD/MM/YYYY');
                }
                if (data.listCourt.startTime != null) data.listCourt.startTime = this.parseDateTime(data.listCourt.startTime, 'HH:mm:ss', true, true);
                if (data.listCourt.endTime != null) data.listCourt.endTime = this.parseDateTime(data.listCourt.endTime, 'HH:mm:ss', true, true);
                var url = this.apiControllerUrl('order');
                url += '/listcourt';
                return axios
                        .request(
                            {
                                method: this.isUuidNullOrEmpty(this.order.orderId) ? 'post' : 'put',
                                url: url,
                                data: JSON.stringify(data),
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            });
            },
        },
        mounted: function () {
            Vue.nextTick().then(() => {
                this.$refs.sittingDateValidator.reset();
            });
        }

        //mounted: function () {
        //    this.reload();
        //}
    }
</script>
